import { useCallback, useEffect, useMemo, useState } from 'react';
import { Toast } from 'antd-mobile';
import i18n from '../../../i18n';
import { referralApi } from '../../../api/referral';
import { ApiEnvelope, Pagination, PaginatedApiEnvelope } from '../../../api/types';
import { ReferralChild, ReferralOverview, ReferralRewardItem, ReferralViewState } from './types';

const DEFAULT_PAGINATION: Pagination = {
  total: 0,
  page: 1,
  pageSize: 10,
  totalPages: 0,
};

function readErrorMessage(error: { response?: { data?: { error?: { message?: string } } } }, fallback: string): string {
  return error.response?.data?.error?.message || fallback;
}

export function useReferralModel() {
  const [state, setState] = useState<ReferralViewState>({
    loading: true,
    overview: null,
    children: [],
    rewards: [],
    childrenPagination: DEFAULT_PAGINATION,
    rewardsPagination: DEFAULT_PAGINATION,
  });

  const loadOverview = useCallback(async () => {
    const response = await referralApi.getMyOverview() as { data: ApiEnvelope<ReferralOverview> };
    return response.data.data;
  }, []);

  const loadChildren = useCallback(async (page: number, pageSize: number) => {
    const response = await referralApi.getMyChildren({ page, pageSize }) as { data: PaginatedApiEnvelope<ReferralChild> };
    return response.data;
  }, []);

  const loadRewards = useCallback(async (page: number, pageSize: number) => {
    const response = await referralApi.getMyRewards({ page, pageSize }) as { data: PaginatedApiEnvelope<ReferralRewardItem> };
    return response.data;
  }, []);

  const refreshAll = useCallback(async () => {
    setState((previous) => ({ ...previous, loading: true }));
    try {
      const [overview, childrenResult, rewardsResult] = await Promise.all([
        loadOverview(),
        loadChildren(state.childrenPagination.page, state.childrenPagination.pageSize),
        loadRewards(state.rewardsPagination.page, state.rewardsPagination.pageSize),
      ]);
      setState({
        loading: false,
        overview,
        children: childrenResult.data,
        rewards: rewardsResult.data,
        childrenPagination: childrenResult.pagination,
        rewardsPagination: rewardsResult.pagination,
      });
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as { response?: { data?: { error?: { message?: string } } } }, i18n.t('referral:loadDataFailed')),
      });
      setState((previous) => ({ ...previous, loading: false }));
    }
  }, [loadChildren, loadOverview, loadRewards, state.childrenPagination.page, state.childrenPagination.pageSize, state.rewardsPagination.page, state.rewardsPagination.pageSize]);

  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  const changeChildrenPage = useCallback(async (nextPage: number) => {
    try {
      const result = await loadChildren(nextPage, state.childrenPagination.pageSize);
      setState((previous) => ({ ...previous, children: result.data, childrenPagination: result.pagination }));
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as { response?: { data?: { error?: { message?: string } } } }, i18n.t('referral:loadChildrenFailed')),
      });
    }
  }, [loadChildren, state.childrenPagination.pageSize]);

  const changeRewardsPage = useCallback(async (nextPage: number) => {
    try {
      const result = await loadRewards(nextPage, state.rewardsPagination.pageSize);
      setState((previous) => ({ ...previous, rewards: result.data, rewardsPagination: result.pagination }));
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as { response?: { data?: { error?: { message?: string } } } }, i18n.t('referral:loadRewardsFailed')),
      });
    }
  }, [loadRewards, state.rewardsPagination.pageSize]);

  const shareUrl = useMemo(() => {
    if (!state.overview) {
      return '';
    }
    return `${window.location.origin}${state.overview.invite_path}`;
  }, [state.overview]);

  return {
    state,
    shareUrl,
    refreshAll,
    changeChildrenPage,
    changeRewardsPage,
  };
}

